import { useRef, useState } from 'react'
import onboardingService from '../../../services/onboardingService'
import { clearProgress } from './onboardingProgress'
import {
    buildLinkedinSequenceNodes,
    persistProspectsAsLeads,
    createDraftLinkedinCampaign,
    resolveCampaignDetails,
    getConnectedSenders,
    launchLinkedinCampaign,
} from './buildLinkedinCampaign'

const useCampaignLaunch = ({ prospects, messageValues, icp, offer }) => {
    const workflowIdRef = useRef(null)
    const creationPromiseRef = useRef(null)
    const [campaignStatus, setCampaignStatus] = useState('idle')
    const [campaignError, setCampaignError] = useState('')

    const failWith = (message) => {
        setCampaignError(message)
        setCampaignStatus('error')
    }

    const ensureCampaignCreated = async () => {
        if (workflowIdRef.current) return workflowIdRef.current
        // Reuse the in-flight creation so overlapping callers (a step transition
        // plus launch/save-draft, or a double render) persist the leads only once
        // instead of racing past the post-completion guard and duplicating them.
        if (creationPromiseRef.current) return creationPromiseRef.current
        creationPromiseRef.current = (async () => {
            const leadIds = await persistProspectsAsLeads(prospects)
            if (leadIds.length) {
                onboardingService
                    .prepareEnrichment({ userLeadIds: leadIds })
                    .catch(() => {})
            }
            const { name, description } = await resolveCampaignDetails({ icp, offer })
            const workflowId = await createDraftLinkedinCampaign({
                name,
                description,
                nodes: buildLinkedinSequenceNodes(messageValues),
                userLeadIds: leadIds,
            })
            workflowIdRef.current = workflowId
            return workflowId
        })()
        try {
            return await creationPromiseRef.current
        } catch (err) {
            creationPromiseRef.current = null // allow a retry after a failure
            throw err
        }
    }

    // "Launch" activates the campaign right away with a schedule prefilled to
    // every day in the connected proxy region's timezone, then drops the user in
    // the campaign manager. "Save as draft" creates the campaign without launching.
    const finishCampaign = async (launch) => {
        setCampaignStatus('working')
        setCampaignError('')
        try {
            const workflowId = await ensureCampaignCreated()
            if (launch) {
                const { senderIds, timezone } = await getConnectedSenders()
                if (!senderIds.length) {
                    throw new Error('Connect a LinkedIn account before launching your campaign.')
                }
                await launchLinkedinCampaign({ workflowId, senderIds, timezone })
            }
            await onboardingService.completeOnboarding()
            clearProgress()
            window.location.assign('/campaign-manager')
        } catch (err) {
            failWith(err.message || 'Something went wrong. Please try again.')
        }
    }

    return {
        campaignStatus,
        campaignError,
        setCampaignStatus,
        ensureCampaignCreated,
        finishCampaign,
        failWith,
    }
}

export default useCampaignLaunch
